import React, { useState, useEffect } from 'react';
import { webLinksAPI } from '../services/api';

const defaultForm = { title: '', url: '', description: '', category: '', image: null, remove_image: false };

// Generates a consistent color from a string
const CARD_COLORS = [
  '#0EBCD4', '#8b5cf6', '#f59e0b', '#22c55e',
  '#ef4444', '#3b82f6', '#ec4899', '#14b8a6',
  '#f97316', '#6366f1',
];
function cardColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return CARD_COLORS[Math.abs(hash) % CARD_COLORS.length];
}

function LinkCard({ link, onEdit, onDelete }) {
  const [hovered, setHovered] = useState(false);
  const color = cardColor(link.title);
  const initial = link.title.charAt(0).toUpperCase();

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'var(--bg-card)', border: '1px solid var(--border-subtle)',
        borderRadius: '12px', overflow: 'hidden', position: 'relative',
        transition: 'transform 0.15s, box-shadow 0.15s',
        transform: hovered ? 'translateY(-3px)' : 'none',
        boxShadow: hovered ? '0 8px 24px rgba(0,0,0,0.25)' : 'var(--shadow-sm)',
        display: 'flex', flexDirection: 'column'
      }}
    >
      {/* Clickable card body */}
      <a href={link.url} target="_blank" rel="noopener noreferrer"
        style={{ textDecoration: 'none', padding: '20px 24px', flex: 1, display: 'flex', flexDirection: 'column', gap: '12px', minHeight: '90px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '10px', flexShrink: 0,
            background: link.image_url ? 'transparent' : color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '22px', fontWeight: 700, color: '#fff', overflow: 'hidden'
          }}>
            {link.image_url
              ? <img src={link.image_url} alt={link.title} style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '10px' }} />
              : initial}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)', marginBottom: '2px',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {link.title}
            </div>
            {link.category && (
              <span style={{
                fontSize: '11px', fontWeight: 600, padding: '1px 7px', borderRadius: '10px',
                background: `${color}22`, color: color
              }}>
                {link.category}
              </span>
            )}
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ flexShrink: 0, color: 'var(--text-muted)' }}>
            <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
            <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
          </svg>
        </div>
        {link.description && (
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0, lineHeight: '1.5' }}>
            {link.description}
          </p>
        )}
        <div style={{ fontSize: '11px', color: 'var(--text-muted)', overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {link.url}
        </div>
      </a>

      {/* Edit/Delete — always visible at bottom of card */}
      <div style={{
        padding: '10px 16px', borderTop: '1px solid var(--border-color)',
        display: 'flex', gap: '8px', justifyContent: 'flex-end',
        background: 'var(--bg-secondary)'
      }}>
        <button onClick={(e) => { e.stopPropagation(); onEdit(link); }}
          className="btn btn-secondary btn-sm">Edit</button>
        <button onClick={(e) => { e.stopPropagation(); onDelete(link.id); }}
          className="btn btn-danger btn-sm">Delete</button>
      </div>
    </div>
  );
}

function WebLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(defaultForm);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  useEffect(() => { fetchLinks(); }, []);

  const fetchLinks = async () => {
    try {
      const res = await webLinksAPI.getAll();
      setLinks(res.data);
    } catch (err) {
      console.error('Error fetching links:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let url = formData.url.trim();
      if (url && !url.match(/^https?:\/\//i)) url = 'https://' + url;
      const fd = new FormData();
      fd.append('title', formData.title);
      fd.append('url', url);
      fd.append('description', formData.description || '');
      fd.append('category', formData.category || '');
      if (formData.image) fd.append('image', formData.image);
      if (formData.remove_image) fd.append('remove_image', 'true');
      if (editingId) { await webLinksAPI.update(editingId, fd); }
      else { await webLinksAPI.create(fd); }
      fetchLinks();
      resetForm();
    } catch (err) {
      console.error('Error saving link:', err);
      alert('Failed to save link');
    }
  };

  const handleEdit = (link) => {
    setFormData({ title: link.title, url: link.url, description: link.description || '', category: link.category || '', image: null, remove_image: false, existing_image: link.image_url || null });
    setEditingId(link.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this link?')) return;
    try { await webLinksAPI.delete(id); fetchLinks(); }
    catch (err) { console.error('Error deleting link:', err); }
  };

  const resetForm = () => { setFormData(defaultForm); setEditingId(null); setShowForm(false); };

  const categories = [...new Set(links.map(l => l.category).filter(Boolean))].sort();

  const filtered = links.filter(l => {
    if (filterCategory && l.category !== filterCategory) return false;
    if (search) {
      const q = search.toLowerCase();
      return l.title.toLowerCase().includes(q) || l.url.toLowerCase().includes(q) || (l.description || '').toLowerCase().includes(q);
    }
    return true;
  });

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  return (
    <div className="container fade-in">
      <div className="page-header">
        <div>
          <h2>Web Links</h2>
          <p>Saved links and bookmarks for your family</p>
        </div>
        <button onClick={() => { setShowForm(!showForm); if (showForm) resetForm(); }} className="btn btn-primary">
          {showForm ? 'Cancel' : '+ Add Link'}
        </button>
      </div>

      {/* Add/Edit form */}
      {showForm && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="form-section">
            <h4 style={{ marginBottom: '16px', fontSize: '14px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {editingId ? 'Edit Link' : 'New Link'}
            </h4>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Title *</label>
                  <input type="text" className="form-input" value={formData.title} required
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g. Family Calendar" />
                </div>
                <div className="form-group">
                  <label className="form-label">URL *</label>
                  <input type="text" className="form-input" value={formData.url} required
                    onChange={e => setFormData({ ...formData, url: e.target.value })}
                    placeholder="https://..." />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <input type="text" className="form-input" value={formData.category}
                    onChange={e => setFormData({ ...formData, category: e.target.value })}
                    placeholder="e.g. Finance, School, Health"
                    list="category-suggestions" />
                  <datalist id="category-suggestions">
                    {categories.map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input type="text" className="form-input" value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Optional note about this link" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Custom Icon Image</label>
                  {formData.existing_image && !formData.remove_image && !formData.image && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <img src={formData.existing_image} alt="Current" style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover' }} />
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Current image</span>
                      <button type="button" onClick={() => setFormData({ ...formData, remove_image: true })}
                        className="btn btn-danger btn-sm">Remove</button>
                    </div>
                  )}
                  {formData.remove_image && (
                    <div style={{ marginBottom: '8px', fontSize: '12px', color: '#ef4444' }}>
                      Image will be removed on save.{' '}
                      <button type="button" onClick={() => setFormData({ ...formData, remove_image: false })}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-coral)', textDecoration: 'underline', fontSize: '12px' }}>
                        Undo
                      </button>
                    </div>
                  )}
                  <input type="file" accept="image/*"
                    onChange={e => setFormData({ ...formData, image: e.target.files[0] || null, remove_image: false })}
                    style={{ fontSize: '13px', color: 'var(--text-secondary)' }} />
                  <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                    PNG, JPG, GIF, WebP — max 2MB. Replaces the letter avatar.
                  </p>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '16px' }}>
                  <div className="flex gap-1">
                    <button type="submit" className="btn btn-success">{editingId ? 'Update' : 'Add'}</button>
                    <button type="button" onClick={resetForm} className="btn btn-secondary">Cancel</button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Search + filter */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '20px' }}>
        <input type="text" className="form-input" placeholder="Search links..." value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: '160px', padding: '8px 12px', fontSize: '13px' }} />
        <select className="form-select" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          style={{ minWidth: '140px', fontSize: '13px' }}>
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span style={{ fontSize: '13px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {filtered.length} link{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">🔗</div>
            <p className="empty-state-text">{links.length === 0 ? 'No links yet' : 'No links match your search'}</p>
            {links.length === 0 && <button onClick={() => setShowForm(true)} className="btn btn-primary">Add Your First Link</button>}
          </div>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 480px), 1fr))',
          gap: '16px'
        }}>
          {filtered.map(link => (
            <LinkCard key={link.id} link={link} onEdit={handleEdit} onDelete={handleDelete} />
          ))}
        </div>
      )}
    </div>
  );
}

export default WebLinks;
